import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  styled,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import {
  Invitation,
  InvitationState,
  UpdateInvitationRequest,
} from '../../../../redux/invitations/invitation.model';
import { getPolyglot } from '../../../../i18n';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getStatusColor, StyledChip } from './components/StyledChip';
import { NormalizedGroup } from '../../../../redux/groups/api/GroupAdapter';
import { findMatchingCompany } from '../../util/invitationsUtilFunctions';

interface InvitationRequestModalProps {
  open: boolean;
  invitation: Invitation | null;
  onClose: () => void;
  onReject: (invitation: Invitation) => void;
  onApprove: (invitation: Partial<UpdateInvitationRequest>) => void;
  availableCompanies: Record<string, NormalizedGroup> | undefined;
  isLoading: boolean;
}

const validationSchema = () =>
  Yup.object().shape({
    name: Yup.string(),
    email: Yup.string().required('freshwatch.invitation.modal.required_email'),
    companyName: Yup.string().required(
      'freshwatch.invitation.modal.required_company_name'
    ),
    phoneNumber: Yup.string(),
  });

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  display: 'block',
  color: theme.palette.text.primary,
}));

export const InvitationRequestModal: React.FC<InvitationRequestModalProps> = ({
  open,
  invitation,
  onClose,
  onReject,
  onApprove,
  availableCompanies = {},
  isLoading,
}) => {
  const polyglot = getPolyglot();
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | undefined
  >(undefined);
  const readMode = invitation?.status !== InvitationState.NEW;

  const formik = useFormik({
    initialValues: {
      name: invitation?.user?.name || '',
      email: invitation?.user?.email || '',
      phoneNumber: invitation?.user?.mobilePhoneNumber || '',
      companyName: invitation?.companyName || '',
      createCompany: false,
    },
    validationSchema: validationSchema(),
    onSubmit: async (values) => {
      if (!formik.isValid) return;
      onApprove({
        ...invitation,
        state: InvitationState.APPROVED,
        companyGroupId: selectedCompanyId,
        companyGroupName: values.companyName,
      });
    },
  });
  React.useEffect(() => {
    setSelectedCompanyId(undefined);
    if (invitation?.companyGroupId) {
      // If invitation already has a company group assigned, use it
      setSelectedCompanyId(invitation.companyGroupId);
    } else if (invitation?.companyName) {
      // Otherwise, try to find a matching company based on the company name
      const matchingCompany = findMatchingCompany(
        invitation.companyName,
        availableCompanies
      );
      if (matchingCompany) {
        setSelectedCompanyId(matchingCompany.id);
      }
    }
  }, [invitation, availableCompanies]);

  if (!invitation) return null;

  const handleReject = () => {
    onReject(invitation);
  };

  const handleApprove = () => {
    onApprove({
      ...invitation,
      state: InvitationState.APPROVED,
      companyGroupId: selectedCompanyId,
      companyGroupName: formik.values.companyName,
    });
  };

  const formatDate = (date: number): string => {
    if (!date) return '-';
    return moment(date).format('YYYY/MM/DD');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ padding: '2rem 0' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant="h2" gutterBottom>
              {polyglot.t('freshwatch.invitation.modal.title')}
            </Typography>
            <StyledChip
              sx={{
                ...getStatusColor(invitation.status as InvitationState),
                width: 'fit-content',
                height: '2rem',
              }}
            >
              {polyglot.t(`freshwatch.invitation.detail.${invitation.status}`)}
            </StyledChip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {polyglot.t('freshwatch.invitation.modal.request_received_at', {
              date: formatDate(invitation.createdAt),
            })}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Name Field */}
            <Grid item xs={12} md={6}>
              <StyledFormLabel htmlFor="name">
                {polyglot.t('freshwatch.invitation.detail.name')}
              </StyledFormLabel>
              <TextField
                fullWidth
                id={'name'}
                value={invitation?.user?.name || ''}
                disabled
                variant="outlined"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                  },
                }}
              />
            </Grid>
            {/* Email Field */}
            <Grid item xs={12} md={6}>
              <StyledFormLabel htmlFor="email">
                {polyglot.t('freshwatch.invitation.detail.email')}
              </StyledFormLabel>

              <TextField
                fullWidth
                value={invitation.email || ''}
                disabled
                variant="outlined"
                id={'email'}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                  },
                }}
              />
            </Grid>

            {/* PhoneNumber Field */}
            <Grid item xs={12} md={6}>
              <StyledFormLabel htmlFor="phoneNumber">
                {polyglot.t('freshwatch.invitation.detail.phone_number')}
              </StyledFormLabel>

              <TextField
                fullWidth
                value={invitation.user?.mobilePhoneNumber || ''}
                disabled
                variant="outlined"
                id={'phoneNumber'}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                  },
                }}
              />
            </Grid>
            {/* Companies Field */}
            <Grid item xs={12} md={6}>
              {(readMode && selectedCompanyId) || !readMode ? (
                <>
                  <StyledFormLabel htmlFor="companiesList">
                    {polyglot.t('freshwatch.invitation.modal.companies_list')}
                  </StyledFormLabel>
                  <TextField
                    fullWidth
                    select
                    value={selectedCompanyId || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedCompanyId(id);
                      const selectedCompany = Object.values(
                        availableCompanies
                      ).find((company) => company.id === id);
                      formik.setFieldValue(
                        'companyName',
                        selectedCompany ? selectedCompany.name : ''
                      );
                    }}
                    variant="outlined"
                    disabled={readMode}
                    SelectProps={{ displayEmpty: true }}
                    placeholder={polyglot.t(
                      'freshwatch.invitation.modal.select_company'
                    )}
                    id={'companiesList'}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="">
                      <em>
                        {polyglot.t('freshwatch.invitation.modal.no_company')}
                      </em>
                    </MenuItem>
                    {Object.values(availableCompanies).map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              ) : null}

              {!readMode ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.createCompany}
                      onChange={() => {
                        formik.setFieldValue(
                          'createCompany',
                          !formik.values.createCompany
                        );
                        setSelectedCompanyId(undefined);
                      }}
                      color="primary"
                      id="createCompany"
                    />
                  }
                  label={
                    <Typography
                      variant={'caption'}
                      color={'text.secondary'}
                      sx={{ mt: 1 }}
                    >
                      {polyglot.t(
                        'freshwatch.invitation.modal.no_matching_company'
                      )}
                    </Typography>
                  }
                />
              ) : null}
              {(formik.values.createCompany && !readMode) ||
              (readMode && !selectedCompanyId) ? (
                <>
                  <StyledFormLabel htmlFor="companies">
                    {polyglot.t('freshwatch.invitation.detail.company_name')}
                  </StyledFormLabel>
                  <TextField
                    fullWidth
                    id={'companyName'}
                    onChange={formik.handleChange}
                    value={formik.values.companyName}
                    disabled={readMode}
                    variant="outlined"
                  />
                </>
              ) : null}

              {!selectedCompanyId &&
              !formik.values.createCompany &&
              !readMode ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {polyglot.t(
                    'freshwatch.invitation.modal.select_company_helper_text'
                  )}
                </Typography>
              ) : null}
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 5,
              gap: 2,
            }}
          >
            {!readMode ? (
              <Button
                variant="outlined"
                onClick={handleReject}
                color="error"
                disabled={isLoading}
              >
                {polyglot.t('freshwatch.invitation.modal.reject')}
              </Button>
            ) : null}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={onClose} disabled={isLoading}>
                {polyglot.t('freshwatch.invitation.modal.back_to_list')}
              </Button>

              {!readMode ? (
                <Button
                  variant="contained"
                  onClick={handleApprove}
                  disabled={
                    (!selectedCompanyId && !formik.values.createCompany) ||
                    isLoading
                  }
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    polyglot.t('freshwatch.invitation.modal.save_and_approve')
                  )}
                </Button>
              ) : null}
            </Box>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvitationRequestModal;
